import { List, Typography, Empty, Button, Input } from "antd";
import { UploadOutlined, SearchOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { KeyboardEvent } from "react";

import { useWorkbenchDocuments } from "../../api/queries";
import type { Document, DocumentFormat } from "../../types";

const { Text } = Typography;

export interface CompareDocumentPickerProps {
  /** Scope to a matter; omit for cross-matter listing (e.g., docs with no matter). */
  matterId?: string;
  /** Doc id to hide from the list (e.g., the current doc — avoid self-compare). */
  excludeDocId?: string;
  onPick: (doc: Document) => void;
  selectedDocId: string | null;
  onUploadClick?: () => void;
}

const FORMAT_LABEL: Record<DocumentFormat, string> = {
  pdf: "PDF",
  docx: "DOCX",
  doc: "DOC",
  xlsx: "XLSX",
  pptx: "PPTX",
  txt: "TXT",
  image: "图片",
  other: "其他",
};

// veridoc's comparison engine only accepts PDF and Word; other formats cannot
// be compared, so they are shown disabled rather than silently failing later.
const COMPARABLE_FORMATS = new Set<DocumentFormat>(["pdf", "docx"]);

export function CompareDocumentPicker({
  matterId,
  excludeDocId,
  onPick,
  selectedDocId,
  onUploadClick,
}: CompareDocumentPickerProps) {
  // Backend caps page_size at settings.pagination_max_page_size (default 100,
  // enforced as 422 per BUG #24). Pulling 100 covers a typical matter's full
  // document set; matters larger than that are rare and would warrant a proper
  // paginated picker rather than a bigger page.
  //
  // Server-side formats filter (veridoc 只接受 pdf/docx) + matterId 缺省时跨事项
  // 列出当前用户可见文档，供无事项文档发起跨文档比对。
  const { data, isLoading } = useWorkbenchDocuments({
    formats: ["pdf", "docx"],
    pageSize: 100,
    ...(matterId ? { matterId } : {}),
  });
  const docs = data?.data ?? [];

  // Title filter — a matter can hold dozens of documents across two slots, so a
  // search box avoids scrolling the full flat list twice to locate a version.
  const [query, setQuery] = useState("");
  const filteredDocs = useMemo(() => {
    const visible = excludeDocId
      ? docs.filter((d) => d.id !== excludeDocId)
      : docs;
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((doc) => doc.title.toLowerCase().includes(q));
  }, [docs, query, excludeDocId]);

  if (!isLoading && docs.length === 0) {
    return (
      <Empty description="没有可选的比对文档">
        {onUploadClick && (
          <Button type="primary" icon={<UploadOutlined />} onClick={onUploadClick}>
            上传文档
          </Button>
        )}
      </Empty>
    );
  }

  const handleKeyDown = (doc: Document) => (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onPick(doc);
    }
  };

  return (
    <div role="listbox" aria-label="选择比对文档">
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="按文档标题搜索"
        aria-label="按文档标题搜索"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <List
        loading={isLoading}
        dataSource={filteredDocs}
        locale={{ emptyText: query ? "未找到匹配的文档" : "没有可选的比对文档" }}
        renderItem={(doc) => {
          const isSelected = selectedDocId === doc.id;
          const formatLabel = FORMAT_LABEL[doc.format] ?? doc.format.toUpperCase();
          const comparable = COMPARABLE_FORMATS.has(doc.format);
          return (
            <List.Item
              data-testid={`compare-doc-${doc.id}`}
              tabIndex={comparable ? 0 : -1}
              role="option"
              aria-selected={comparable && isSelected}
              aria-disabled={!comparable}
              aria-label={
                comparable
                  ? `${doc.title}，${formatLabel}`
                  : `${doc.title}，${formatLabel}（不支持比对）`
              }
              onClick={comparable ? () => onPick(doc) : undefined}
              onKeyDown={comparable ? handleKeyDown(doc) : undefined}
              style={{
                cursor: comparable ? "pointer" : "not-allowed",
                opacity: comparable ? 1 : 0.45,
                background:
                  comparable && isSelected
                    ? "var(--jerry-selected-bg)"
                    : undefined,
              }}
            >
              <List.Item.Meta
                title={doc.title}
                description={
                  <Text type="secondary">
                    {formatLabel}
                    {!comparable && " · 仅支持 PDF / Word 比对"}
                  </Text>
                }
              />
            </List.Item>
          );
        }}
      />
    </div>
  );
}

export default CompareDocumentPicker;
